import * as React from "react";
import { Button } from "@/components/ui/button";
import { Card, CardHeader } from "@/components/ui/card";
import {
  Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogForm, DialogHeader,
  DialogTitle, DiscardGuard,
} from "@/components/ui/dialog";
import { Callout } from "@/components/ui/feedback";
import { Field } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/toast";
import { ApiError } from "@/lib/api";
import { useRetentionDue, useSetDomainRetention } from "@/lib/queries";
import {
  MAX_RETENTION_DAYS, choiceOf, daysError, daysLabel, inForce, ownFor, recordingsLabel, shortens,
  sourceLabel, windowLabel, type RetentionChoice,
} from "@/lib/retention";
import type { Domain } from "@/lib/types";

/**
 * How long each domain keeps a call's recording and its transcripts (`retention_days`).
 *
 * Under the transcription card because it is the next question about the same content: that one
 * decides whether a call becomes text, this one how long the audio and the text are kept. It is
 * the firm's number, so an admin sets it; a supervisor reads it here and cannot change it,
 * because "how long do you keep my call" is asked of whoever is on the floor.
 *
 * A row shows the window in force and where it comes from, never the stored value alone: a domain
 * with no number of its own follows the platform's, and "Follows the platform" with nothing
 * beside it would not answer the question.
 *
 * Changing it is a dialog, not an inline field. A shorter window deletes the calls already
 * stored, within the hour, and that cannot be undone, so the form says how many recordings that
 * is before it lets the number be saved.
 */
export function DomainRetentionCard({
  domains, canManage,
}: {
  domains: Domain[];
  canManage: boolean;
}) {
  const [editing, setEditing] = React.useState<Domain | undefined>();
  if (domains.length === 0) return null;
  return (
    <Card className="mt-4">
      <CardHeader
        title="Keep recordings and transcripts"
        description="How long a call's recording and its transcripts are kept, counted from the day of the call. After that they are deleted for good; the call's record stays."
      />
      <ul className="divide-y divide-border">
        {domains.map((d) => (
          <li key={d.id} className="flex items-center justify-between gap-4 px-4 py-2.5">
            <span className="font-mono text-xs text-text">{d.name}</span>
            <span className="flex items-center gap-3">
              <span className="text-right">
                <span className="block text-xs font-medium text-text">
                  {windowLabel(d.effective_retention_days)}
                </span>
                <span className="block text-xs text-text-muted">
                  {sourceLabel(d.retention_days)}
                </span>
              </span>
              {canManage && (
                <Button
                  size="sm"
                  aria-label={`Change how long ${d.name} keeps recordings and transcripts`}
                  onClick={() => setEditing(d)}
                >
                  Change
                </Button>
              )}
            </span>
          </li>
        ))}
      </ul>
      <DomainRetentionDialog
        open={Boolean(editing)}
        onOpenChange={(open) => !open && setEditing(undefined)}
        domain={editing}
      />
    </Card>
  );
}

function DomainRetentionDialog({
  open, onOpenChange, domain,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  domain?: Domain;
}) {
  const toast = useToast();
  const save = useSetDomainRetention();
  const [choice, setChoice] = React.useState<RetentionChoice>("platform");
  const [daysText, setDaysText] = React.useState("");
  const [touched, setTouched] = React.useState(false);
  const [formError, setFormError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open || !domain) return;
    setFormError(null);
    setTouched(false);
    setChoice(choiceOf(domain.retention_days));
    setDaysText(domain.retention_days ? String(domain.retention_days) : "");
  }, [open, domain]);

  const platform = domain?.platform_retention_days ?? 0;
  const current = domain?.effective_retention_days ?? 0;
  // `undefined` while "a number of days" is chosen and the field holds no usable number.
  const own = ownFor(choice, daysText);
  const next = own === undefined ? undefined : inForce(own, platform);
  const changed = domain !== undefined && own !== undefined && own !== domain.retention_days;
  const deletes = changed && next !== undefined && shortens(current, next);

  // Asked only for a change that deletes, and only for the number about to be saved.
  const due = useRetentionDue(domain?.id, next ?? 0, open && deletes);
  const count = deletes ? due.data?.recordings : undefined;
  const counted = !deletes || count !== undefined;
  const fieldError = choice === "days" && touched ? daysError(daysText) : undefined;

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setFormError(null);
    setTouched(true);
    if (!domain || own === undefined || !counted) return;
    if (!changed) {
      onOpenChange(false);
      return;
    }
    try {
      const saved = await save.mutateAsync({ id: domain.id, retention_days: own });
      toast.success(
        "Retention window saved",
        `${domain.name}: ${windowLabel(saved.effective_retention_days).toLowerCase()}`,
      );
      onOpenChange(false);
    } catch (e) {
      setFormError(e instanceof ApiError ? e.message : "Could not save the window.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DiscardGuard changed={changed} />
        <DialogHeader>
          <DialogTitle>Keep recordings and transcripts</DialogTitle>
          <DialogDescription>
            How long <span className="font-mono">{domain?.name}</span> keeps a call's recording
            and its transcripts, counted from the day of the call.
          </DialogDescription>
        </DialogHeader>

        <DialogForm onSubmit={onSubmit} noValidate>
          <DialogBody className="space-y-4">
            {formError && <Callout tone="danger">{formError}</Callout>}

            <fieldset className="space-y-2">
              <legend className="text-xs font-medium text-text">Window</legend>
              <RetentionOption
                checked={choice === "platform"}
                onSelect={() => setChoice("platform")}
                title="Follow the platform"
                description={
                  platform > 0
                    ? `The platform's window. Right now that is ${daysLabel(platform)} after the call.`
                    : "The platform's window. Right now the platform keeps everything."
                }
              />
              <RetentionOption
                checked={choice === "keep"}
                onSelect={() => setChoice("keep")}
                title="Keep everything"
                description="Nothing of this domain's is deleted, whatever the platform's window is."
              />
              <RetentionOption
                checked={choice === "days"}
                onSelect={() => setChoice("days")}
                title="Delete after a number of days"
                description="This domain's own window. A recording and its transcripts are deleted that many days after the call."
              />
            </fieldset>

            {choice === "days" && (
              <Field
                label="Days"
                htmlFor="retention-days"
                hint={`A whole number from 1 to ${MAX_RETENTION_DAYS}.`}
                error={fieldError}
              >
                <Input
                  inputMode="numeric"
                  className="w-32"
                  value={daysText}
                  invalid={Boolean(fieldError)}
                  onChange={(e) => setDaysText(e.target.value)}
                  onBlur={() => setTouched(true)}
                  autoFocus
                />
              </Field>
            )}

            {deletes && next !== undefined && (
              <DeletesCallout
                days={next}
                count={count}
                failed={due.isError}
                onRetry={() => void due.refetch()}
              />
            )}
          </DialogBody>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant={deletes && count ? "danger" : "default"}
              loading={save.isPending}
              disabled={!counted}
            >
              {deletes && count ? `Save and delete ${recordingsLabel(count)}` : "Save"}
            </Button>
          </DialogFooter>
        </DialogForm>
      </DialogContent>
    </Dialog>
  );
}

/**
 * What a shorter window is about to do, said before it is saved. The count is the domain's
 * recordings older than the new window; Save stays off until it is known, because a number
 * nobody has seen is not something to confirm.
 */
function DeletesCallout({
  days, count, failed, onRetry,
}: {
  days: number;
  count: number | undefined;
  failed: boolean;
  onRetry: () => void;
}) {
  if (failed) {
    return (
      <Callout tone="danger">
        Could not check how many recordings this would delete, so it cannot be saved yet.{" "}
        <button type="button" className="font-medium underline" onClick={onRetry}>
          Try again
        </button>
      </Callout>
    );
  }
  if (count === undefined) {
    return <Callout tone="warning">Checking how many recordings this would delete…</Callout>;
  }
  if (count === 0) {
    return (
      <Callout tone="warning">
        No recording of this domain is older than {daysLabel(days)} yet. From now on a recording
        and its transcripts are deleted {daysLabel(days)} after the call. This cannot be undone.
      </Callout>
    );
  }
  return (
    <Callout tone="danger">
      {recordingsLabel(count)} of this domain {count === 1 ? "is" : "are"} older than{" "}
      {daysLabel(days)}. {count === 1 ? "It" : "They"} will be deleted within the hour, with{" "}
      {count === 1 ? "its" : "their"} transcripts. This cannot be undone.
    </Callout>
  );
}

function RetentionOption({
  checked, onSelect, title, description,
}: {
  checked: boolean;
  onSelect: () => void;
  title: string;
  description: string;
}) {
  return (
    <label className="flex cursor-pointer items-start gap-2.5 rounded-md border border-border px-3 py-2.5 has-[:checked]:border-accent-8 has-[:checked]:bg-accent-2">
      <input
        type="radio"
        name="retention-window"
        className="mt-0.5 accent-accent-9"
        checked={checked}
        onChange={onSelect}
      />
      <span>
        <span className="block text-xs font-medium text-text">{title}</span>
        <span className="block text-xs text-text-muted">{description}</span>
      </span>
    </label>
  );
}
